'use client'

import cn from 'clsx'
import { useLenis } from 'lenis/react'
import { usePathname } from 'next/navigation'
import { useEffect, useRef, useState } from 'react'
import { Glyphs } from '~/components/button'
import { Link } from '~/components/link'
import { useGlobalTransitionRefs } from '~/components/page-transition/context'
import { Squares } from '~/components/squares'
import { useDeviceDetection } from '~/hooks/use-device-detection'
import { useStore } from '~/libs/store'
import s from './header.module.css'
import { MobileMenu } from './mobile-menu/mobile-menu'

// Define NAVITEMS here
const NAVITEMS = [
  {
    label: 'Open Source',
    href: 'https://github.com/darkroomengineering',
    order: 1,
  },
  {
    label: 'Work',
    href: '/work',
    order: 2,
  },
  {
    label: 'About',
    href: '/about',
    order: 3,
  },
  {
    label: 'Read',
    href: '/read',
    order: 4,
  },
  // {
  //   label: 'Store',
  //   href: '/store',
  //   order: 5,
  // },
  {
    label: 'Contact',
    href: '/contact',
    order: 6,
  },
  {
    label: 'Become an open source sponsor',
    href: 'https://github.com/sponsors/darkroomengineering',
    order: 7,
  },
]

export function Header() {
  const lenis = useLenis()
  const { isMobile } = useDeviceDetection()
  const isFooterFullyInView = useStore((state) => state.isFooterFullyInView)
  const hasAppeared = useStore((state) => state.hasAppeared)
  const setHasAppeared = useStore((state) => state.setHasAppeared)
  const headerRef = useRef(null)

  useGlobalTransitionRefs({
    header: headerRef,
  })

  const pathname = usePathname()

  const [appearStep, setAppearStep] = useState(-2)

  const squaresRefs = useRef([])

  useEffect(() => {
    const done = () => {
      setAppearStep(4)
      setHasAppeared(true)
      lenis?.start()
    }

    // if (process.env.NODE_ENV === 'development') {
    //   done()
    //   return
    // }

    if (!lenis || hasAppeared) return
    lenis.stop()

    const interval = setInterval(() => {
      for (const square of squaresRefs.current) {
        square?.animate(1000, {
          duration: 0,
        })
      }
    }, 100)

    const timeout = setTimeout(() => {
      setAppearStep(-1)
    }, 1000)

    const timeout0 = setTimeout(() => {
      setAppearStep(0)
    }, 2000)

    const timeout1 = setTimeout(() => {
      setAppearStep(1)
    }, 3000)

    const timeout2 = setTimeout(() => {
      setAppearStep(2)
    }, 4000)

    const timeout3 = setTimeout(() => {
      setAppearStep(3)
      lenis?.start()
    }, 5000)

    const timeout4 = setTimeout(() => {
      done()
      clearInterval(interval)
    }, 6000)

    return () => {
      clearTimeout(timeout)
      clearTimeout(timeout0)
      clearTimeout(timeout1)
      clearTimeout(timeout2)
      clearTimeout(timeout3)
      clearTimeout(timeout4)

      clearInterval(interval)
      lenis?.start()
    }
  }, [lenis, hasAppeared, setHasAppeared])

  // if (appearStep !== 4) {
  //   lenis?.stop()
  // } else {
  //   lenis?.start()
  // }

  return (
    <div
      ref={headerRef}
      className={cn(
        s.wrapper,
        appearStep > -2 && s.appear0,
        appearStep > -1 && s.appear1,
        appearStep > 0 && s.appear2,
        appearStep > 1 && s.appear3,
        appearStep > 2 && s.appear4,
        appearStep > 3 && s.appear5,
        isFooterFullyInView && s.hidden,
        'z-20'
      )}
    >
      <header
        className={cn(
          s.header,
          'absolute top-0 left-0 right-0 p uppercase dr-layout-grid-inner h-header-height bg-primary'
        )}
      >
        <div
          className={cn(
            'sp-8 h-full dt:spy-6 dt:spx-8 flex items-center justify-center bg-contrast absolute left-safe',
            s.left
          )}
        >
          <Glyphs className={cn(s.glyphs, 'flex items-center mobile-only')} />
          <Squares
            className="sh-10 desktop-only"
            single={isMobile}
            ref={(node) => {
              squaresRefs.current[0] = node
            }}
          />
        </div>
        <div
          className={cn(
            'relative col-start-2 col-end-4 dt:col-start-3 dt:col-end-8 h-full flex justify-center items-center dt:grid dt:grid-cols-5 dt:gap-gap'
          )}
        >
          <Link
            className={cn(
              s.logo,
              'cta spx-8 flex items-center h-full bg-contrast cta-hover',
              'dt:col-span-2 dt:justify-self-start'
            )}
            href="/"
          >
            darkroom.engineering
          </Link>
          <nav className={cn(s.nav, 'flex desktop-only col-span-2')}>
            {NAVITEMS
              // Filter out external links (starting with http)
              .filter((item) => !item.href.startsWith('http'))
              // Sort by order
              .sort((a, b) => a.order - b.order)
              // Map to Link components
              .map(({ label, href, order }) => {
                // Check if this link's href matches the current pathname
                const isActive = pathname === href

                return (
                  <Link
                    key={order} // Use order as a more stable key
                    className={cn(
                      'cta spy-4 spx-8 flex items-center bg-contrast cta-hover',
                      // Conditionally add the active class
                      isActive && s.active
                    )}
                    href={href}
                  >
                    {label}
                  </Link>
                )
              })}
          </nav>
        </div>
        <div
          className={cn(
            s.right,
            'sw-28 sh-28 dt:h-fit dt:w-fit dt:spy-6 dt:spx-8 dt:flex bg-contrast dt:items-center dt:justify-center absolute right-safe'
          )}
        >
          <Squares
            mirrored
            className="sh-10 desktop-only"
            ref={(node) => {
              squaresRefs.current[1] = node
            }}
          />
          <MobileMenu
            navItems={NAVITEMS}
            className="mobile-only"
          />
        </div>
      </header>
    </div>
  )
}
